import type { Messages } from "@/i18n/locales/en";
import { legalMessagesHr } from "@/i18n/locales/legalHrData";
import { pagesMessagesHr } from "@/i18n/locales/pagesHrData";

export const hrMessages = {
  meta: {
    description:
      "WasOK — skenirajte proizvode, spremite ocjene i bilješke, zapamtite što se isplatilo kupiti.",
    title: "WasOK — Zapamtite što se isplatilo kupiti",
  },
  header: {
    navFeatures: "Značajke",
    navHowItWorks: "Kako funkcionira",
    navPrivacy: "Privatnost",
    navUseCases: "Primjeri upotrebe",
    download: "Preuzmi",
    openMenu: "Otvori izbornik",
    closeMenu: "Zatvori izbornik",
    languageAria: "Jezik",
  },
  hero: {
    badge: "📱 Sada dostupno na Androidu",
    title: "Zapamtite što se isplatilo kupiti.",
    subtitle:
      "Skenirajte proizvode, spremite ocjene i bilješke te odmah zapamtite je li proizvod stvarno bio dobar.",
    googlePlay: "Preuzmi s Google Playa",
    watchDemo: "Pogledaj demo",
    privacyLine: "Svi podaci ostaju na vašem uređaju.",
    phoneTitle: "WasOK",
    phoneSubtitle: "Skenirajte da zapamtite",
    snackCard: "Ukusno!",
    coffeeCard: "Previše gorka",
    scrollHint: "Pomaknite se i istražite",
  },
  problems: {
    heading: "Zvuči poznato?",
    subheading:
      "Svi smo to doživjeli — gledamo proizvode i pokušavamo se sjetiti jesu li nam se prošli put svidjeli.",
    notedAgo: "✓ Zabilježili ste ovo prije 3 mjeseca",
    items: [
      {
        question: "Koja mi je čokolada stvarno odgovarala?",
        note: "Savršena slatkoća",
      },
      {
        question: "Zašto sam prestao kupovati ovaj šampon?",
        note: "Isušio mi je kosu",
      },
      {
        question: "Je li ova kava bila dobra ili razočaravajuća?",
        note: "Previše gorka",
      },
    ],
  },
  howItWorks: {
    heading: "Kako funkcionira",
    subheading:
      "Četiri jednostavna koraka da više nikad ne zaboravite svoja mišljenja o proizvodima.",
    stepPrefix: "Korak",
    steps: [
      {
        title: "Skenirajte bilo koji barkod proizvoda",
        description:
          "Brzo i jednostavno skeniranje kamerom. Radi s bilo kojim barkodom.",
      },
      {
        title: "Spremite ocjene, komentare i fotografije",
        description:
          "Ocijenite proizvode, dodajte bilješke, priložite fotografije te zabilježite cijene i lokacije.",
      },
      {
        title: "Odmah se sjetite svog iskustva",
        description:
          "Kasnije skenirajte isti proizvod i vidite prethodnu ocjenu i bilješke.",
      },
      {
        title: "Sastavite pametne popise za kupovinu",
        description:
          "Stvarajte popise za kupovinu od proizvoda kojima već vjerujete i koje volite.",
      },
    ],
  },
  privacy: {
    heading: "Vaša povijest kupovine ostaje vaša.",
    body: "WasOK vam pomaže zapamtiti proizvode koje ste isprobali, dok ocjene, bilješke, fotografije i popise za kupovinu pohranjuje lokalno na vašem uređaju.",
    trustBanner:
      "💚 Građen oko vaše povijesti kupovine — ne oko prikupljanja vaših podataka.",
    bullets: [
      "Račun nije potreban",
      "Sinkronizacija u oblaku nije potrebna",
      "Lokalna pohrana",
      "Sigurnosne kopije pod kontrolom korisnika",
      "Opcionalno pretraživanje barkoda za informacije o proizvodu.",
      "Podržano oglasima putem Google AdMoba",
    ],
  },
  features: {
    heading: "Sve što vam treba",
    subheading:
      "Snažne značajke osmišljene kako bi praćenje proizvoda bilo jednostavno i privatno.",
    items: [
      {
        title: "Skener barkodova",
        description: "Brzo i precizno skeniranje barkoda za bilo koji proizvod.",
      },
      {
        title: "Ocjene proizvoda",
        description: "Ocijenite proizvode od 1 do 5 zvjezdica kako biste zapamtili kvalitetu.",
      },
      {
        title: "Fotografije proizvoda",
        description: "Priložite fotografije da zapamtite ambalažu ili detalje.",
      },
      {
        title: "Kategorije",
        description: "Organizirajte proizvode u prilagođene kategorije.",
      },
      {
        title: "Praćenje trgovina",
        description: "Zapamtite gdje ste kupili svaki proizvod.",
      },
      {
        title: "Bilježenje cijena",
        description: "Pratite cijene i pronađite najbolje ponude.",
      },
      {
        title: "Popisi za kupovinu",
        description: "Sastavljajte popise od omiljenih proizvoda.",
      },
      {
        title: "Offline pristup",
        description: "Savršeno radi i bez internetske veze.",
      },
      {
        title: "Lokalna pohrana",
        description: "Svi vaši podaci sigurno su pohranjeni na vašem uređaju.",
      },
    ],
  },
  useCases: {
    heading: "Savršeno za svaki red police",
    subheading:
      "Bilo da kupujete hranu, kozmetiku ili kućanske potrepštine — WasOK vam pomaže zapamtiti.",
    cards: [
      {
        emoji: "🍫",
        title: "Grickalice i slatkiši",
        description: "Zapamtite koje su slatkiše stvarno bili dobri.",
      },
      {
        emoji: "☕",
        title: "Kava i pića",
        description: "Pratite kave koje se isplati ponovno kupiti.",
      },
      {
        emoji: "💄",
        title: "Kozmetika i njega kože",
        description: "Bilježite reakcije kože.",
      },
      {
        emoji: "🧹",
        title: "Kućanske potrepštine",
        description: "Izbjegnite ponovnu kupnju proizvoda koji su razočarali.",
      },
    ],
  },
  screenshots: {
    heading: "Lijepo i intuitivno",
    subheading:
      "Pažljivo dizajnirano sučelje koje praćenje proizvoda čini užitkom.",
    items: [
      { title: "Skener" },
      { title: "Detalj proizvoda" },
      { title: "Unos ocjene" },
      { title: "Pretraga i povijest" },
      { title: "Popis za kupovinu" },
    ],
  },
  cta: {
    heading: "Memorija za kupovinu u stvarnom životu.",
    body: "Prestanite zaboravljati koji su se proizvodi isplatili. Počnite kupovati pametnije već danas.",
    googlePlay: "Preuzmi s Google Playa",
    scanQr: "Skenirajte za preuzimanje",
    tagline: "Privatno · Offline · Jednostavno",
  },
  footer: {
    tagline: "Zapamtite što se isplatilo kupiti.",
    linksHeading: "Poveznice",
    privacyPolicy: "Politika privatnosti",
    terms: "Uvjeti korištenja",
    contact: "Kontakt",
    linkFaq: "FAQ",
    linkLicenses: "Licence otvorenog koda",
    linkAdvertising: "Oglašavanje i monetizacija",
    downloadHeading: "Preuzmi",
    googlePlay: "Google Play",
    copyright:
      "© 2026 WasOK. Sva prava pridržana. Napravljeno s 💚 za pametniju kupovinu.",
  },
  legal: legalMessagesHr,
  faq: pagesMessagesHr.faq,
  support: pagesMessagesHr.support,
  advertising: pagesMessagesHr.advertising,
} satisfies Messages;
